import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '~/lib/api-client';
import { attentionKeys, reflectKeys, feedKeys, dayOneKeys } from '~/lib/query-keys';
import type { ReflectProposalEdit } from '~/features/memory/reflection-edit-draft';

/** FRONT-369 / ADR-0029: reflect review inbox data layer. */

export type ReflectProposalKind =
  | 'entity_create'
  | 'attribute_update'
  | 'relationship_create'
  | 'eddy_update'
  | 'soul_update'
  // FRONT-894 ("commitment catcher"): a commitment Eddy caught in chat,
  // pre-attached to an existing project container. Surfaces
  // exclusively on the Work board's "Needs your OK" strip
  // (see use-commitments.ts) — the Memory reflections inbox filters this
  // kind out of its display (reflections-view.tsx).
  | 'task_create'
  | 'perfect_day_update';

export type ReflectProposalStatus = 'pending' | 'applied' | 'rejected';

export interface ReflectProposal {
  id: number;
  runId: number;
  kind: ReflectProposalKind;
  // Shape varies by kind; the card renders defensively off whatever is present.
  payload: Record<string, unknown>;
  rationale: string;
  source: string | null;
  targetEntityId: string | null;
  status: ReflectProposalStatus;
  appliedAt: string | null;
  /** FRONT-206: why automatic mode held this back instead of auto-applying it.
   *  Null unless the earned-entity gate withheld it; accepting it anyway is
   *  always allowed. */
  gateReason: string | null;
  createdAt: string;
  /** FRONT-731: entity names the server resolved off the payload's id
   *  references (never a raw id) — see src/reflect/describe-proposal.ts. The
   *  label/description shown in the review inbox is built entirely on the
   *  frontend from these plus `payload`. */
  targetEntityName: string | null;
  fromEntityName: string | null;
  toEntityName: string | null;
}

export interface ReflectRun {
  id: number;
  windowStart: string | null;
  windowEnd: string;
  mode: 'approval' | 'automatic';
  status: 'success' | 'failed' | 'noop';
  sourceCounts: { chat: number; documents: number; files: number; graphContext: number };
  proposalCount: number;
  errorMessage: string | null;
  createdAt: string;
}

export type ReflectionMode = 'approval' | 'automatic';

/** FRONT-2498: what happened to one proposal, as the Reflection page shows it. */
export type ProposalOutcome = 'applied' | 'pending' | 'held_back' | 'not_applied';

/** Where a proposal came from. `target` is set only when the server confirmed
 *  it is still the viewer's own (a chat session id, a document filename, or a
 *  file id); otherwise the page shows the kind of source with no link. */
export interface ProposalSource {
  kind: 'chat' | 'doc' | 'file';
  label: string | null;
  target: string | null;
}

export interface RunProposal extends ReflectProposal {
  outcome: ProposalOutcome;
  /** One-line form of `gateReason`, or null when there is no good one. */
  holdReasonShort: string | null;
  sourceLink: ProposalSource | null;
}

export function useReflectProposals(status: ReflectProposalStatus = 'pending') {
  return useQuery({
    queryKey: reflectKeys.proposals(status),
    queryFn: () =>
      api.get<{ proposals: ReflectProposal[] }>(
        `/api/reflect/proposals?status=${status}`,
      ),
    select: (d) => d.proposals,
  });
}

/**
 * All of a user's proposals regardless of status — for a caller that needs
 * to tell "already decided" apart from "still pending" for a SPECIFIC run
 * (FRONT-1014, Marko live-test: `OnboardingRecapCard`'s decided/accepted
 * status used to be local-only `useState`, which reset to pending on every
 * remount — reopening the day-one chat always re-showed an already-decided
 * recap as if it never happened). Own cache entry (`status: undefined`),
 * distinct from `useReflectProposals`'s default-'pending' one.
 */
export function useReflectProposalsAll() {
  return useQuery({
    queryKey: reflectKeys.proposals(),
    queryFn: () => api.get<{ proposals: ReflectProposal[] }>('/api/reflect/proposals'),
    select: (d) => d.proposals,
  });
}

export function useReflectRuns() {
  return useQuery({
    queryKey: reflectKeys.runs(),
    queryFn: () => api.get<{ runs: ReflectRun[] }>('/api/reflect/runs'),
    select: (d) => d.runs,
  });
}

/** FRONT-2498: one of the viewer's runs and what it proposed, fetched when its row is opened. */
export function useReflectRunProposals(runId: number) {
  return useQuery({
    queryKey: reflectKeys.runProposals(runId),
    queryFn: () =>
      api.get<{ runId: number; proposals: RunProposal[] }>(
        `/api/reflect/runs/${encodeURIComponent(String(runId))}/proposals`,
      ),
    select: (d) => d.proposals,
  });
}

export function useReflectionMode() {
  return useQuery({
    queryKey: reflectKeys.mode(),
    queryFn: () => api.get<{ mode: ReflectionMode }>('/api/reflect/mode'),
    select: (d) => d.mode,
  });
}

export function errorMessage(err: unknown, fallback: string): string {
  // FRONT-2499: a spent compute budget answers with the platform's refusal,
  // whose `error` is a code and whose `message` is the sentence for the person.
  const body = (err as { body?: { error?: unknown; message?: unknown } } | null)?.body;
  if (body?.error === 'usage_limit_reached' && typeof body.message === 'string' && body.message) {
    return body.message;
  }
  if (err instanceof Error && err.message) {
    try {
      const parsed = JSON.parse(err.message);
      if (parsed?.error) return parsed.error;
    } catch {
      /* not JSON */
    }
    return err.message.slice(0, 200);
  }
  return fallback;
}

/** Accept or reject one proposal. */
export function useProposalAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'accept' | 'reject' }) =>
      api.post(`/api/reflect/proposals/${id}/${action}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: reflectKeys.all });
      // FRONT-1488: the sidebar badge counts this queue.
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      // FRONT-1029: see useBatchProposalAction — an individual accept can
      // also be the one that approves an `onboarding:interview` proposal
      // (the Memory inbox doesn't filter that source out), so it owes the
      // feed the same invalidation.
      if (vars.action === 'accept') qc.invalidateQueries({ queryKey: feedKeys.all });
      // FRONT-1014 (Marko, live-test): same reason — an individual accept
      // from the Memory inbox can be the one that decides an onboarding
      // interview recap, and the day-one chat's "Enter Eddy" gate reads
      // `interviewRecapDecided` off this same query. Without this it would
      // sit on the 30s staleTime instead of unlocking immediately.
      qc.invalidateQueries({ queryKey: dayOneKeys.all });
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not update the proposal')),
  });
}

/**
 * FRONT-2349: accept one proposal with the user's edit (Reflections Edit and
 * the onboarding recap's per-row Edit). The server rebuilds the proposal from
 * its stored original; `edited` is true only when the edit changed it. No
 * error toast here — a refusal is written for the person and the card shows
 * it next to the form it is about.
 */
export function useAcceptProposalWithEdit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, edit }: { id: number; edit: ReflectProposalEdit }) =>
      api.post<{ ok: boolean; edited?: boolean; reason?: string }>(`/api/reflect/proposals/${id}/accept`, { edit }),
    onSuccess: () => {
      // Same caches a plain accept refreshes — see useProposalAction.
      qc.invalidateQueries({ queryKey: reflectKeys.all });
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      qc.invalidateQueries({ queryKey: feedKeys.all });
      qc.invalidateQueries({ queryKey: dayOneKeys.all });
    },
  });
}

/**
 * FRONT-2349: reject a few proposals as one decision — the onboarding recap's
 * "Leave this out" (one row) and the Key people row (the people list plus each
 * person's entry and connection). One refresh at the end, not one per id.
 */
export function useRejectProposals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      // Every id is attempted even when one fails, so a blip on one person
      // doesn't leave the rest of the group pending for "Save the rest" to
      // sweep up. A 409 means already decided elsewhere — settled either way.
      const results = await Promise.allSettled(ids.map((id) => api.post(`/api/reflect/proposals/${id}/reject`)));
      const failed = results.filter(
        (r) => r.status === 'rejected' && !(r.reason instanceof ApiError && r.reason.status === 409),
      );
      if (failed.length > 0) {
        throw new Error(
          failed.length === ids.length
            ? "That couldn't be left out. Try again."
            : `Part of that couldn't be left out (${failed.length} of ${ids.length}). Try Leave out again.`,
        );
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: reflectKeys.all });
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      qc.invalidateQueries({ queryKey: dayOneKeys.all });
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not leave that out')),
  });
}

/** Accept or reject every pending proposal (optionally scoped to one run). */
export function useBatchProposalAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, runId }: { action: 'accept' | 'reject'; runId?: number }) =>
      api.post(`/api/reflect/proposals/${action}-all`, runId ? { runId } : {}),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: reflectKeys.all });
      // FRONT-1488: the sidebar badge counts this queue.
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      // FRONT-1014 (Marko, 2026-07-29): the reflect proposal already
      // EXISTED (in 'pending' status) the moment `capture_perfect_day_answers`
      // staged it, well before either button is clicked — `isInterviewRecapDecided`
      // is existence-based now, not decision-based, so this invalidation is
      // really just "make sure the day-one/feed caches catch up promptly",
      // not "only accept counts". Both actions get it.
      qc.invalidateQueries({ queryKey: dayOneKeys.all });
      if (vars.action === 'accept') {
        // FRONT-1029: an accept can flip the "finish your setup interview"
        // completion signal — without this the card would linger in the
        // inbox until the feed's own 5-minute refetch.
        qc.invalidateQueries({ queryKey: feedKeys.all });
      }
      toast.success(vars.action === 'accept' ? 'Applied to your graph' : 'Dismissed');
    },
    onError: (err) => toast.error(errorMessage(err, 'Batch action failed')),
  });
}

export interface ReflectRunSummary {
  runId: number;
  status: 'success' | 'failed' | 'noop';
  proposalCount: number;
  applied: number;
  withheld: number;
  mode: ReflectionMode;
}

/** FRONT-505: "Reflect now" — trigger a reflection for the current user on
 *  demand. Strictly owner-scoped server-side; returns the real outcome so we
 *  can surface "nothing new" vs. proposals-to-review vs. applied. Refreshes the
 *  proposals + runs lists so they update without a page reload. */
export function useRunReflectNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ summary: ReflectRunSummary }>('/api/reflect/run'),
    onSuccess: ({ summary }) => {
      qc.invalidateQueries({ queryKey: reflectKeys.all });
      // FRONT-1488: the sidebar badge counts this queue.
      qc.invalidateQueries({ queryKey: attentionKeys.all });
      if (summary.status === 'failed') {
        toast.error('Reflection failed — please try again');
        return;
      }
      if (summary.status === 'noop' || summary.proposalCount === 0) {
        toast.success('Nothing new to reflect on');
        return;
      }
      if (summary.mode === 'automatic') {
        toast.success(`Reflected — ${summary.applied} applied to your graph`);
        return;
      }
      toast.success(
        `Reflected — ${summary.proposalCount} ${summary.proposalCount === 1 ? 'proposal' : 'proposals'} to review`,
      );
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not start a reflection')),
  });
}

export function useSetReflectionMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: ReflectionMode) => api.put('/api/reflect/mode', { mode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: reflectKeys.mode() }),
    onError: (err) => toast.error(errorMessage(err, 'Could not change reflection mode')),
  });
}

/** FRONT-509: the per-user reflection on/off flag. Absent ⇒ on (server default).
 *  Off drops the user from the scheduled fan-out; "Reflect now" still works. */
export function useReflectionEnabled() {
  return useQuery({
    queryKey: reflectKeys.enabled(),
    queryFn: () => api.get<{ enabled: boolean }>('/api/reflect/enabled'),
    select: (d) => d.enabled,
  });
}

export function useSetReflectionEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.put('/api/reflect/enabled', { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: reflectKeys.enabled() }),
    onError: (err) => toast.error(errorMessage(err, 'Could not change reflection setting')),
  });
}
